import React, { useEffect, useRef } from "react";
import {
  Gauge,
  ShieldAlert,
  RefreshCw,
  BadgeCheck,
  Route,
  ClipboardCheck,
} from "lucide-react";

import "../../css/Bento.css";

const BentoItem = ({ className = "", children }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      el.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
      el.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <div ref={ref} className={`fl-bento-item ${className}`}>
      {children}
    </div>
  );
};

const IconBadge = ({ icon: Icon }) => (
  <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-wash text-brand-magenta ring-1 ring-brand-orchid/30">
    <Icon className="h-5 w-5" />
  </span>
);

const ConnectedWorkflow = () => {
  return (
    <section id="platform" className="relative bg-white font-body">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
        {/* header */}
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-brand-bg px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            One platform
          </span>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
            One Connected Workflow for{" "}
            <span className="text-brand-gradient">Asset Recovery</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink sm:text-lg">
            Furloop brings the recovery lifecycle into one platform — helping teams process devices from
            intake to resale-ready status with clearer decisions, less manual rework, and stronger evidence.
          </p>
        </div>

        {/* bento */}
        <div className="fl-bento-grid mt-14">
          {/* large */}
          <BentoItem className="fl-col-2 fl-row-2 justify-between">
            <div>
              <IconBadge icon={Gauge} />
              <h3 className="font-display text-2xl font-semibold text-brand-plum">Process faster</h3>
              <p className="mt-2 max-w-md text-ink">
                Move devices through erasure, assessment, reimaging, grading, and resale with less
                technician effort — one record, updated at every step.
              </p>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { k: "3.2×", v: "faster throughput" },
                { k: "−61%", v: "manual rework" },
                { k: "1", v: "asset record" },
              ].map((s) => (
                <div
                  key={s.v}
                  className="rounded-xl border border-line bg-brand-bg/70 p-3 text-center"
                >
                  <div className="font-display text-xl font-semibold text-brand-magenta">{s.k}</div>
                  <div className="mt-0.5 text-[11px] leading-tight text-muted">{s.v}</div>
                </div>
              ))}
            </div>
          </BentoItem>

          <BentoItem>
            <IconBadge icon={ShieldAlert} />
            <h3 className="font-display text-lg font-semibold text-brand-plum">Reduce failed jobs</h3>
            <p className="mt-2 text-sm text-ink">
              Use preflight checks to catch blockers before starting destructive or billable processing steps.
            </p>
          </BentoItem>

          <BentoItem>
            <IconBadge icon={RefreshCw} />
            <h3 className="font-display text-lg font-semibold text-brand-plum">Reuse what works</h3>
            <p className="mt-2 text-sm text-ink">
              Capture driver and workflow knowledge once, then reuse known-good profiles for matching devices.
            </p>
          </BentoItem>

          <BentoItem className="fl-row-2 justify-between">
            <div>
              <IconBadge icon={BadgeCheck} />
              <h3 className="font-display text-lg font-semibold text-brand-plum">Improve resale confidence</h3>
              <p className="mt-2 text-sm text-ink">
                Create device reports backed by assessment results, erasure status, grading evidence, and
                reimage verification.
              </p>
            </div>
            <div className="mt-6 space-y-2">
              {["Erasure certificate", "Assessment report", "Grade A · photos"].map((t) => (
                <div
                  key={t}
                  className="flex items-center gap-2 rounded-lg border border-line bg-white px-3 py-2 text-xs font-medium text-brand-plum"
                >
                  <BadgeCheck className="h-4 w-4 text-success" />
                  {t}
                </div>
              ))}
            </div>
          </BentoItem>

          <BentoItem className="fl-col-2">
            <IconBadge icon={Route} />
            <h3 className="font-display text-lg font-semibold text-brand-plum">Route devices clearly</h3>
            <p className="mt-2 max-w-xl text-sm text-ink">
              Send each asset to resale, redeployment, repair, quarantine, parts, or recycling based on real
              device status — no guesswork, no dead-end batches.
            </p>
          </BentoItem>

          <BentoItem className="fl-col-2">
            <IconBadge icon={ClipboardCheck} />
            <h3 className="font-display text-lg font-semibold text-brand-plum">Build operational proof</h3>
            <p className="mt-2 max-w-xl text-sm text-ink">
              Keep one asset record holding device identity, test results, photos, certificates, workflow
              history, and final outcome.
            </p>
          </BentoItem>
        </div>
      </div>
    </section>
  );
};

export default ConnectedWorkflow;
